import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, HandCoins } from 'lucide-react';
import { PERMISSIONS, PaymentMethod } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCancelPlanMutation,
  usePaymentPlanQuery,
  useSettlePlanMutation,
} from '../api/receivables.api';

const STATE_TONES: Record<string, 'neutral' | 'warning' | 'danger' | 'success' | 'brand'> = {
  UPCOMING: 'neutral',
  DUE: 'brand',
  PARTIALLY_PAID: 'warning',
  OVERDUE: 'danger',
  PAID: 'success',
  CANCELLED: 'neutral',
};

/**
 * One payment plan, instalment by instalment.
 *
 * Taking money here spreads oldest-due-first, which is what the counter wants by default — the
 * customer hands over what they have and it answers the oldest debt. Directing it at a particular
 * instalment is a manager's act and lives behind `payments.allocate`, so it is not offered here.
 */
export function PaymentPlanPage() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { format, parse, toInput } = useCurrency();
  const navigate = useNavigate();
  const { planId = '' } = useParams<{ planId: string }>();

  const plan = usePaymentPlanQuery(planId);
  const settle = useSettlePlanMutation(planId);
  const cancel = useCancelPlanMutation(planId);

  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settleOpen, setSettleOpen] = useState(false);
  const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.CASH);
  const [amount, setAmount] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  if (plan.isPending) return <LoadingState />;
  if (plan.isError || !plan.data) {
    return (
      <Alert tone="error">
        {apiErrorMessage(plan.error) ?? t('sales.receivables.errors.load')}
      </Alert>
    );
  }

  const data = plan.data;
  const isActive = data.status === 'ACTIVE';
  const canTakeMoney = can(PERMISSIONS.PAYMENTS_CREATE);
  const canCancel = can(PERMISSIONS.SALES_REFUND) && data.allocatedMinor === 0;

  const openSettle = () => {
    setAmount(toInput(data.outstandingMinor));
    setSettleOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={data.reference}
        subtitle={[data.customerName, data.branchName].filter(Boolean).join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => navigate('/sales/receivables')}>
              <ArrowLeft className="size-4 rtl:rotate-180" aria-hidden />
              {t('sales.receivables.backToList')}
            </Button>
            {isActive && canTakeMoney ? (
              <Button onClick={openSettle}>
                <HandCoins className="size-4" aria-hidden />
                {t('sales.receivables.takePayment')}
              </Button>
            ) : null}
            {isActive && canCancel ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)}>
                {t('sales.receivables.cancelPlan')}
              </Button>
            ) : null}
          </div>
        }
      />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {feedback ? <Alert tone="success">{feedback}</Alert> : null}

      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={isActive ? 'brand' : data.status === 'COMPLETED' ? 'success' : 'neutral'}>
          {t(`sales.receivables.planStatuses.${data.status}`)}
        </Badge>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/sales/orders/${data.orderId}`)}>
          <span className="font-mono" dir="ltr">
            {data.orderReference}
          </span>
        </Button>
        <span className="text-muted text-xs">
          {t('sales.receivables.financed', { amount: format(data.totalFinancedMinor) })}
        </span>
        <span className="text-ink ms-auto font-medium" dir="ltr">
          {format(data.outstandingMinor)}
          <span className="text-muted ms-2 text-xs">{t('sales.receivables.stillOwing')}</span>
        </span>
      </div>

      {data.status === 'CANCELLED' && data.cancelledReason ? (
        <Alert tone="warning">
          {t('sales.receivables.cancelledBecause', { reason: data.cancelledReason })}
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('sales.receivables.schedule')}
          subtitle={t('sales.receivables.scheduleHint')}
        />
        <ul className="divide-line divide-y">
          {data.installments.map((line) => (
            <li key={line.id} className="flex flex-wrap items-center gap-3 p-4">
              <span className="text-muted w-8 font-mono text-sm" dir="ltr">
                {line.sequence}
              </span>

              <span className="min-w-0 flex-1">
                <span className="text-ink block font-medium" dir="ltr">
                  {line.dueDate}
                </span>
                {line.daysOverdue ? (
                  <span className="text-xs text-red-700">
                    {t('sales.receivables.daysLate', { count: line.daysOverdue })}
                  </span>
                ) : null}
              </span>

              <span className="flex flex-col text-end text-xs" dir="ltr">
                <span className="text-ink font-medium">{format(line.amountMinor)}</span>
                {line.allocatedMinor > 0 && line.outstandingMinor > 0 ? (
                  <span className="text-muted">
                    {t('sales.receivables.paidOf', {
                      paid: format(line.allocatedMinor),
                    })}
                  </span>
                ) : null}
              </span>

              <Badge tone={STATE_TONES[line.state] ?? 'neutral'}>
                {t(`sales.receivables.states.${line.state}`)}
              </Badge>
            </li>
          ))}
        </ul>
      </Card>

      <Modal
        isOpen={settleOpen}
        onClose={() => setSettleOpen(false)}
        title={t('sales.receivables.takePayment')}
        description={t('sales.receivables.takePaymentHint')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setSettleOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              isLoading={settle.isPending}
              disabled={(parse(amount) ?? 0) <= 0}
              onClick={() => {
                const amountMinor = parse(amount);
                if (!amountMinor || amountMinor <= 0) return;

                settle.mutate(
                  { method, amountMinor },
                  {
                    onSuccess: () => {
                      setSettleOpen(false);
                      setError(null);
                      setFeedback(t('sales.receivables.paymentTaken'));
                    },
                    onError: (caught) => {
                      setSettleOpen(false);
                      setFeedback(null);
                      setError(apiErrorMessage(caught) ?? t('sales.receivables.errors.settle'));
                    },
                  },
                );
              }}
            >
              {t('sales.receivables.confirmPayment')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <FormField label={t('sales.fields.method')}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={Object.values(PaymentMethod).map((value) => ({
                  value,
                  label: t(`sales.methods.${value}`),
                }))}
                value={method}
                onChange={(event) => setMethod(event.target.value as PaymentMethod)}
              />
            )}
          </FormField>

          <FormField
            label={t('sales.fields.amount')}
            hint={t('sales.receivables.outstandingIs', {
              amount: format(data.outstandingMinor),
            })}
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            )}
          </FormField>

          <Alert tone="info">{t('sales.receivables.oldestFirst')}</Alert>
        </div>
      </Modal>

      <Modal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={t('sales.receivables.cancelPlan')}
        description={t('sales.receivables.cancelPlanHint')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={cancel.isPending}
              disabled={cancelReason.trim().length < 3}
              onClick={() =>
                cancel.mutate(cancelReason.trim(), {
                  onSuccess: () => {
                    setCancelOpen(false);
                    setCancelReason('');
                    setError(null);
                    setFeedback(t('sales.receivables.planCancelled'));
                  },
                  onError: (caught) => {
                    setCancelOpen(false);
                    setFeedback(null);
                    setError(apiErrorMessage(caught) ?? t('sales.receivables.errors.cancel'));
                  },
                })
              }
            >
              {t('sales.receivables.cancelPlan')}
            </Button>
          </>
        }
      >
        <FormField label={t('sales.receivables.cancelReason')} required>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={cancelReason}
              maxLength={500}
              onChange={(event) => setCancelReason(event.target.value)}
            />
          )}
        </FormField>
      </Modal>
    </div>
  );
}
